import { computed, Injectable, signal } from '@angular/core';
import {
  AuthService,
  getCurrentStepInProgress,
  ConferenceGuestsService,
  ConferenceRegistrantsService,
  ConferenceRegistrationLogsService,
} from 'ag-common-svc/public-api';
import { ColumnButtonClickEvent } from 'devextreme/ui/data_grid';
import {
  ChangeSourceType,
  ConferenceRegistrationLog,
  ConferenceRegistrationStepName,
  FlightInfoKeys,
  GuestData,
  GuestKeys,
  GuestStatus,
  LookupKeys,
  RegistrantData,
  RegistrantGuestConfigurationSteps,
  RegistrantKeys,
  RegistrantModelKeys,
  RegistrantWizardStateKeys,
  RequestOutcomeKeys,
  RequestOutcomeState,
  WizardStepState,
} from 'ag-common-lib/public-api';
import { defer, firstValueFrom, Observable, shareReplay } from 'rxjs';
import { AttendeeDetailsModalService } from '../attendee-details-modal.service';
import { catchError, map, tap } from 'rxjs/operators';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { deleteField } from 'firebase/firestore';
import {
  BatchCommand,
  BatchCommandKeys,
  BatchCommandType,
  BatchOperationsService,
} from 'ag-common-svc/lib/dao/batch-firestore.service';

@Injectable()
export class AttendeeGuestsService {
  // Derived from the modal service's guests rather than fetching them a second time: that stream is
  // what showModal$ holds the load panel on, so tabs built from it can no longer paint the previous
  // registrant's guests in the window between the registrant landing and the guests landing.
  private registrantGuests$: Observable<GuestData[]> =
    this.attendeeDetailsModalService.registrantGuests$.pipe(shareReplay(1));

  registrantVisibleGuests$ = this.registrantGuests$.pipe(
    map(guests => {
      return guests?.filter(guest => {
        const guestStatus = guest?.[GuestKeys.guestStatus];
        const invisibleStatusesSet = new Set([GuestStatus.deleted]);

        return !invisibleStatusesSet.has(guestStatus);
      });
    }),
  );
  registrantActiveGuests$ = this.registrantGuests$.pipe(
    map(guests => {
      return guests?.filter(guest => {
        const guestStatus = guest?.[GuestKeys.guestStatus];
        const disabledStatusesSet = new Set([GuestStatus.canceled, GuestStatus.deleted]);

        return !disabledStatusesSet.has(guestStatus);
      });
    }),
  );

  registrantGuestsSig = toSignal(this.registrantGuests$);
  registrantVisibleGuestsSig = toSignal(this.registrantVisibleGuests$);
  registrantActiveGuestsSig = toSignal(this.registrantActiveGuests$);

  wizardGuestStepStateLookup$: Observable<any[]>;

  isRequestedAdditionalGuestSig = computed(() => {
    const registrant = this.attendeeDetailsModalService.registrantSig();
    const registrantData = registrant?.[RegistrantModelKeys.data];
    const isRequestedAdditionalGuest = registrantData?.[RegistrantKeys.additionalGuestRequested];

    return isRequestedAdditionalGuest;
  });

  additionalGuestRequestOutcomeSig = computed(() => {
    const registrant = this.attendeeDetailsModalService.registrantSig();
    const registrantData = registrant?.[RegistrantModelKeys.data];
    const additionalGuestRequestOutcome = registrantData?.[RegistrantKeys.additionalGuestRequestOutcome];

    return additionalGuestRequestOutcome;
  });

  conferenceGuestExcludedSections$ = this.attendeeDetailsModalService.conferenceGuestExcludedSections$;

  private _sourceType = ChangeSourceType.regularArm;

  constructor(
    private authService: AuthService,
    private batchOperationsService: BatchOperationsService,
    private attendeeDetailsModalService: AttendeeDetailsModalService,
    private conferenceRegistrantsService: ConferenceRegistrantsService,
    private conferenceRegistrationLogsService: ConferenceRegistrationLogsService,
  ) {
    this.wizardGuestStepStateLookup$ = this.attendeeDetailsModalService.registrant$.pipe(
      map(registrant => {
        const wizardState = registrant?.[RegistrantModelKeys.wizardState] ?? {};
        const lookup = [];
        Object.entries(wizardState)?.forEach(([stepId, stepState]) => {
          const isGuestState = stepId?.startsWith(ConferenceRegistrationStepName.registrantGuestConfigurationStep);
          if (isGuestState) {
            lookup.push({
              [LookupKeys.value]: stepId.replace(
                `${ConferenceRegistrationStepName.registrantGuestConfigurationStep}_`,
                '',
              ),
              [LookupKeys.description]: stepState,
            });
          }
        });

        return lookup;
      }),
    );
  }

  onGuestCanceled = async (e: ColumnButtonClickEvent<GuestData>) => {
    const guestData = e?.row?.data;
    const registrantGuestDbI = guestData?.[BaseModelKeys.dbId];
    const conferenceDbId = guestData?.[GuestKeys.conferenceDbId];
    const registrantDbId = guestData?.[GuestKeys.registrantDbId];

    const [registrant, conference] = await Promise.all([
      firstValueFrom(this.attendeeDetailsModalService.registrant$),
      firstValueFrom(this.attendeeDetailsModalService.conference$),
    ]);
    const wizardState = registrant?.[RegistrantModelKeys.wizardState] ?? {};
    const isWizardLocked = !!wizardState?.[RegistrantWizardStateKeys.isSubmitted];
    const currentStep = getCurrentStepInProgress({ conference, conferenceRegistration: registrant });
    const isSubmittedRegistration =
      isWizardLocked || currentStep?.name === ConferenceRegistrationStepName.registrationSummaryStep;

    const table = ConferenceGuestsService.getCollectionPath(conferenceDbId, registrantDbId);
    const mainCommand: BatchCommand = {
      [BatchCommandKeys.table]: table,
      [BatchCommandKeys.dbId]: registrantGuestDbI,
      [BatchCommandKeys.data]: {
        [BaseModelKeys.actionName]: this._sourceType,
        [GuestKeys.guestStatus]: GuestStatus.canceled,
        [GuestKeys.flightInformation]: {
          [FlightInfoKeys.driverLicenseNameConfirmation]: false,
          [FlightInfoKeys.passportConfirmation]: false,
          [FlightInfoKeys.passportNameConfirmation]: false,
        },
      },
      [BatchCommandKeys.type]: BatchCommandType.update,
    };

    const commands: BatchCommand[] = [mainCommand];
    if (!isSubmittedRegistration) {
      const wizardStateChanges = {
        [`${ConferenceRegistrationStepName.registrantGuestConfigurationStep}_${registrantGuestDbI}`]: deleteField(),
        [`${ConferenceRegistrationStepName.registrantChairmanExtensionGuestsStep}_${registrantGuestDbI}`]: deleteField(),
        [`${ConferenceRegistrationStepName.registrantFlightInformationStep}_${registrantGuestDbI}`]: deleteField(),
        [`${ConferenceRegistrationStepName.registrantExcursionsStep}_${registrantGuestDbI}`]: deleteField(),
      };

      commands.unshift({
        [BatchCommandKeys.table]: ConferenceRegistrantsService.getCollectionPath(conferenceDbId),
        [BatchCommandKeys.dbId]: registrantDbId,
        [BatchCommandKeys.data]: {
          [BaseModelKeys.actionName]: this._sourceType,
          [RegistrantModelKeys.wizardState]: wizardStateChanges,
        },
        [BatchCommandKeys.type]: BatchCommandType.update,
      });
    }

    await this.batchOperationsService.runBatchOperations(commands);
  };

  onGuestRestored = async (e: ColumnButtonClickEvent<GuestData>) => {
    const guestData = e?.row?.data;
    const registrantGuestDbI = guestData?.[BaseModelKeys.dbId];
    const conferenceDbId = guestData?.[GuestKeys.conferenceDbId];
    const registrantDbId = guestData?.[GuestKeys.registrantDbId];

    if (!registrantGuestDbI || !conferenceDbId || !registrantDbId) {
      return;
    }

    const table = ConferenceGuestsService.getCollectionPath(conferenceDbId, registrantDbId);
    const restoreCommand: BatchCommand = {
      [BatchCommandKeys.table]: table,
      [BatchCommandKeys.dbId]: registrantGuestDbI,
      [BatchCommandKeys.data]: {
        [BaseModelKeys.actionName]: this._sourceType,
        [GuestKeys.guestStatus]: GuestStatus.default,
      },
      [BatchCommandKeys.type]: BatchCommandType.update,
    };

    await this.batchOperationsService.runBatchOperations([restoreCommand]);
  };

  setWizardStepStateToDone = async (guestData: GuestData) => {
    const registrantGuestDbI = guestData?.[BaseModelKeys.dbId];
    const conferenceDbId = guestData?.[GuestKeys.conferenceDbId];
    const registrantDbId = guestData?.[GuestKeys.registrantDbId];
    const stepKey: RegistrantGuestConfigurationSteps = `${ConferenceRegistrationStepName.registrantGuestConfigurationStep}_${registrantGuestDbI}`;

    await this.conferenceRegistrantsService.update(
      conferenceDbId,
      registrantDbId,
      {
        [RegistrantModelKeys.wizardState]: {
          [stepKey]: WizardStepState.done,
        },
      },
      this._sourceType,
      true,
    );
  };

  handleAdditionalGuestRequestOutcome = async (requestOutcomeState: RequestOutcomeState) => {
    const loggedInAgent = await firstValueFrom(this.authService.loggedInAgent$);
    const loggedInAgentDbId = loggedInAgent?.[BaseModelKeys.dbId];

    return this.saveRequestOutcome({
      [RegistrantKeys.additionalGuestRequestOutcome]: {
        [RequestOutcomeKeys.state]: requestOutcomeState,
        [RequestOutcomeKeys.processedBy]: loggedInAgentDbId,
      },
    });
  };

  saveLog = (log: ConferenceRegistrationLog) => {
    const conferenceDbId = this.attendeeDetailsModalService.conferenceDbIdSig();
    const registrantDbId = this.attendeeDetailsModalService.registrantDbIdSig();

    return this.conferenceRegistrationLogsService.create(conferenceDbId, registrantDbId, log);
  };

  private saveRequestOutcome = async (registrantData: Partial<RegistrantData>) => {
    const conferenceDbId = this.attendeeDetailsModalService.conferenceDbIdSig();
    const registrantDbId = this.attendeeDetailsModalService.registrantDbIdSig();

    return this.conferenceRegistrantsService.update(
      conferenceDbId,
      registrantDbId,
      {
        [RegistrantModelKeys.data]: registrantData,
      },
      this._sourceType,
      true,
    );
  };
}
